import React from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import {
  SITE_FOOTER_COPYRIGHT,
  SITE_FOOTER_LEGAL_LINKS,
  SITE_SOCIAL_LINKS,
  SOCIAL_ICON_SIZE,
} from '../../util/siteSocials';
import NamedLink from '../../components/NamedLink/NamedLink';
import { EmergencyLine } from '../../components/EmergencyHelp/EmergencyHelp';

import { HELP_CENTER_LINK_PROPS } from '../../util/helpCenter';
import { openHelpWidget } from '../../util/helpScoutBeacon';
import css from './SiteFooter.module.css';
import InviteStrip from '../../components/InviteStrip/InviteStrip';

const SocialIcon = ({ link, fill }) => {
  if (link.variant === 'instagram') {
    if (fill) {
      return (
        <svg
          viewBox="0 0 24 24"
          width={SOCIAL_ICON_SIZE}
          height={SOCIAL_ICON_SIZE}
          aria-hidden="true"
          focusable="false"
        >
          <rect x="2" y="2" width="20" height="20" rx="5.5" fill="none" stroke={fill} strokeWidth="1.7" />
          <circle cx="12" cy="12.15" r="4.55" fill="none" stroke={fill} strokeWidth="1.7" />
          <circle cx="17.15" cy="6.85" r="1.2" fill={fill} />
        </svg>
      );
    }
    return (
      <svg
        viewBox="0 0 24 24"
        width={SOCIAL_ICON_SIZE}
        height={SOCIAL_ICON_SIZE}
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient id="sl-ig" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0" stopColor="#F9CE34" />
            <stop offset="0.45" stopColor="#EE2A7B" />
            <stop offset="1" stopColor="#6228D7" />
          </linearGradient>
        </defs>
        <rect x="2" y="2" width="20" height="20" rx="5.5" fill="url(#sl-ig)" />
        <circle cx="12" cy="12.15" r="4.55" fill="none" stroke="#fff" strokeWidth="1.85" />
        <circle cx="17.15" cy="6.85" r="1.2" fill="#fff" />
      </svg>
    );
  }

  if (link.variant === 'tiktok') {
    if (fill) {
      return (
        <svg
          viewBox="0 0 24 24"
          width={SOCIAL_ICON_SIZE}
          height={SOCIAL_ICON_SIZE}
          aria-hidden="true"
          focusable="false"
        >
          <path fill={fill} d={link.path} />
        </svg>
      );
    }
    return (
      <svg
        viewBox="0 0 24 24"
        width={SOCIAL_ICON_SIZE}
        height={SOCIAL_ICON_SIZE}
        aria-hidden="true"
        focusable="false"
      >
        <path fill="#69C9D0" d={link.path} transform="translate(1.15 .7)" />
        <path fill="#EE1D52" d={link.path} transform="translate(-.85 -.45)" />
        <path fill="#000000" d={link.path} />
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 24 24"
      width={SOCIAL_ICON_SIZE}
      height={SOCIAL_ICON_SIZE}
      aria-hidden="true"
      focusable="false"
    >
      <path fill={fill || link.fill} d={link.path} />
    </svg>
  );
};

export const SocialLinks = ({
  listClassName,
  linkClassName,
  links = SITE_SOCIAL_LINKS,
  iconFill,
}) => (
  <nav aria-label="Social">
    <ul className={listClassName}>
      {links.map(link => (
        <li key={link.href}>
          <a
            href={link.href}
            className={linkClassName}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={link.label}
          >
            <SocialIcon link={link} fill={iconFill} />
          </a>
        </li>
      ))}
    </ul>
  </nav>
);

/**
 * Landing-style footer for every main page. Do not mount on live session /
 * Join chat UI.
 */
const LayoutFooter = ({ hideEmergency = false } = {}) => {
  return (
    <>
    <InviteStrip />
    <footer className={css.root}>
      <div className={css.inner}>
        <NamedLink name="LandingPage" className={css.brand}>
          <img
            className={css.lockup}
            src="/static/branding/sweet-listeners-original-logo.png"
            alt="Sweet Listeners"
          />
        </NamedLink>
        <p className={css.motto}>
          <FormattedMessage id="SiteFooter.motto" />
        </p>
        <p className={css.notTherapy}>
          <FormattedMessage id="SiteFooter.notTherapy" />
        </p>
        {hideEmergency ? null : <EmergencyLine placement="footer" />}
        <SocialLinks listClassName={css.socialList} linkClassName={css.socialLink} />
        <nav className={css.legal} aria-label="Legal">
          {SITE_FOOTER_LEGAL_LINKS.map((link, index) => (
            <span key={link.href}>
              {index > 0 ? (
                <span className={css.legalDot} aria-hidden="true">
                  ·
                </span>
              ) : null}
              <NamedLink name={link.name} className={css.legalLink} to={link.to}>
                {link.label}
              </NamedLink>
            </span>
          ))}
          <span>
            <span className={css.legalDot} aria-hidden="true">
              ·
            </span>
            <a className={css.legalLink} href="/help" data-footer-help-widget="true" onClick={openHelpWidget}>
              Help
            </a>
          </span>
          <span>
            <span className={css.legalDot} aria-hidden="true">
              ·
            </span>
            <a className={css.legalLink} data-footer-help-center="true" {...HELP_CENTER_LINK_PROPS}>
              Help Center
            </a>
          </span>
        </nav>
        <p className={css.copyright}>{SITE_FOOTER_COPYRIGHT}</p>
      </div>
    </footer>
    </>
  );
};

export { LayoutFooter };
export default LayoutFooter;
